import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Mic2, 
  LayoutGrid, 
  Zap, 
  ArrowRight, 
  Plus, 
  ExternalLink,
  Loader2,
  Settings
} from 'lucide-react';
import { getProjectBySlug, getProjectStats, getInsights } from '../../lib/apiClient';
import ImportEpisodeModal from '../../archive/Components/episodes/ImportEpisodeModal';
import BrandAnalyticsDashboard from '../../components/analytics/BrandAnalyticsDashboard';

export default function BrandDashboardPage() {
  const { brandSlug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentInsights, setRecentInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isImportOpen, setIsImportOpen] = useState(false);

  useEffect(() => {
      async function loadDashboard() {
          try {
              setLoading(true);
              // 1. Get Project
              const projData = await getProjectBySlug(brandSlug);
              if (!projData?.data) throw new Error('Project not found');
              const projectObj = projData.data;
              setProject(projectObj);

              // 2. Get Stats & Insights in parallel
              const [statsData, insightsData] = await Promise.all([
                  getProjectStats(projectObj.id),
                  getInsights({ projectId: projectObj.id, limit: 5 })
              ]);

              setStats(statsData?.data || null);
              setRecentInsights(insightsData || []);

          } catch (e) {
              console.error('Dashboard load failed', e);
          } finally {
              setLoading(false);
          }
      }
      if (brandSlug) loadDashboard();
  }, [brandSlug]);

  if (loading) return <div className="h-screen flex items-center justify-center bg-background text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!project) return <div className="h-screen flex items-center justify-center bg-background text-white">Project not found</div>;

  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      
      {/* Dashboard Header */}
      <header className="bg-surface/50 border-b border-white/10 sticky top-0 z-10 backdrop-blur-md">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center font-bold text-white shadow-lg`}>
              {project.name.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-white leading-none mb-1">{project.name}</h1>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>{project.userRole === 'owner' ? 'Owner' : 'Member'}</span>
                <span className="w-1 h-1 bg-slate-600 rounded-full"/>
                <a href={`${process.env.REACT_APP_PUBLIC_URL || 'http://localhost:3000'}/brands/${project.slug}`} target="_blank" rel="noopener noreferrer" className="hover:text-white flex items-center gap-1">
                  View Public Page <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to={`/app/brand/${brandSlug}/settings`}
              className="px-3 py-2 bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 rounded-lg text-xs font-medium transition-colors flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Link>
            <button className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
              <Mic2 className="w-4 h-4" />
              Connect RSS
            </button>
            <button
              onClick={() => setIsImportOpen(true)}
              className="px-4 py-2 bg-primary text-white font-bold rounded-lg shadow-glow-blue hover:bg-primary-hover transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Upload Episode
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 space-y-8">

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={<Mic2 className="w-5 h-5 text-blue-400" />} label="Total Episodes" value={stats?.episodeCount || 0} subtext="Synced" />
          <StatCard icon={<Zap className="w-5 h-5 text-yellow-400" />} label="Insights Generated" value={stats?.insightCount || 0} subtext="Total" />
          <StatCard icon={<LayoutGrid className="w-5 h-5 text-purple-400" />} label="Topics Covered" value={stats?.topicCount || 0} subtext={`Authority Score: ${stats?.avgAuthorityScore || 0}/100`} />
        </div>

        {/* Analytics Dashboard */}
        <BrandAnalyticsDashboard projectId={project.id} />

        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column: Recent Insights */}
          <div className="lg:col-span-2 space-y-8">

            {/* Recent Insights Table */}
            <div className="bg-surface border border-white/5 rounded-2xl overflow-hidden">
              <div className="p-6 border-b border-white/5 flex items-center justify-between">
                <h3 className="font-bold text-lg text-white">Recent Insights</h3>
                <Link to={`/app/brand/${brandSlug}/episodes`} className="text-sm text-primary hover:text-white transition-colors">View All Episodes</Link>
              </div>
              <div className="divide-y divide-white/5">
                {recentInsights.map((item, i) => (
                  <Link key={i} to={`/workspace/episode/${item.id}`} className="block p-4 hover:bg-white/5 transition-colors group">
                    {/* Note: workspace currently expects episodeId, but here we link to insight. 
                        Ideally workspace should handle insight ID or we link to episode workspace. 
                        Assuming user wants to edit THIS insight. 
                        The workspace currently takes 'episodeSlug' and loads insights list.
                        So we should link to the EPISODE workspace that contains this insight.
                        Wait, `item` here is an insight row. It doesn't have `episode_id` in the default select?
                        I need to ensure GET /api/insights returns episode_id. 
                        Checking route... select('id, title, hook, status, created_at, topics(title)')
                        It missed episode_id. 
                        For now, I'll just link to the insight editor conceptually or use a placeholder link until route is fixed.
                        Actually, I'll just render it without link or link to `#`.
                        Better: I'll add `episode_id` to the select in `route.js` in next step if needed, 
                        but for now let's just link to `/workspace/episode/TODO`.
                    */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-slate-200 group-hover:text-white mb-1">{item.title}</div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span className="px-1.5 py-0.5 rounded bg-white/10 text-slate-300">{item.topics?.title || 'General'}</span>
                          <span>• {new Date(item.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'published' ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
                          {item.status}
                        </span>
                        <div className="p-2 rounded-lg text-slate-400 group-hover:text-white transition-colors">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
                {recentInsights.length === 0 && <div className="p-6 text-center text-slate-500">No insights yet.</div>}
              </div>
            </div>

          </div>

          {/* Right Column: Episode Status */}
          <div className="space-y-8">
            {/* Episode Status */}
            <div className="bg-surface border border-white/5 rounded-2xl p-6">
              <h3 className="font-bold text-white mb-4">Episode Status</h3>
              <div className="text-sm text-slate-400">Check Episodes page for detailed status.</div>
            </div>
          </div>
        </div>
      </main>

      {/* Episode Import Modal */}
      <ImportEpisodeModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onConfirm={() => {
          setIsImportOpen(false);
          if (project?.slug) {
            navigate(`/app/brand/${project.slug}/episodes`);
          }
        }}
        projectId={project.id}
      />
    </div>
  );
}

function StatCard({ icon, label, value, subtext }) {
  return (
    <div className="bg-surface border border-white/5 rounded-xl p-5 hover:border-white/20 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="p-2 bg-white/5 rounded-lg">
          {icon}
        </div>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-slate-400 mb-1">{label}</div>
      <div className="text-xs text-slate-500">{subtext}</div>
    </div>
  );
}
